import { Component, OnInit, Input } from '@angular/core';
import { FormGroup, FormControl, FormArray, FormBuilder, Validators } from '@angular/forms';

import { LunaDomain } from '../../../../builder/models/domain.model';
import { Organization, OrganizationComplianceDetails, FinancialComplianceSearchSpec } from '../../../models/organization.model';
import { DomainDefinitionService } from '../../../../builder/services/domain-definition.service';
import { OrganizationService } from '../../../services/organization.service';
import { ValidatorPatterns } from '../../../classes/validator-patterns';
import { Address } from '../../../models/address.model';
import { AuthService } from '../../../../services/auth.service';
import { Contact, AgentType } from '../../../../admin/models/contact.model';
import { Subscription, BehaviorSubject } from 'rxjs';
import { map, tap, finalize } from "rxjs/operators";

@Component({
    selector: 'organization-form',
    templateUrl: './organization-form.component.html',
    standalone: false
})
export class OrganizationFormComponent implements OnInit {

  blankAddress: Address = {
    street: '',
    suite: '',
    poBox: '',
    city: '',
    stateCode: '',
    zipCode: '',
  };

  dbaReuseParentInfo: boolean = false;
  emergencyContactExists: boolean;
  emergencyContact: Contact;
  orgForm: FormGroup;
  orgForms: FormArray;
  parentOrgForm: FormGroup;
  primaryContact: Contact;
  primaryContactExists: boolean;
  showDba: boolean = this.authService.User.isInternal ? false : true;
  states: LunaDomain;
  temporaryOrganization: Organization;
  orgCompliance: OrganizationComplianceDetails;
  subscriptions: Subscription = new Subscription();

  @Input() organization: Organization;
  @Input() parentOrganization: Organization;
    @Input() readOnly: boolean = false;


    _isRecalculating: boolean = false;

    get isRecalculating(): boolean {
        return this._isRecalculating;
    }

    set isRecalculating(value: boolean) {
        this._isRecalculating = value;
    }

    loadingCompliance: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    get complianceIsLoading(): boolean {
        return this.loadingCompliance.value;
    }

  constructor(public domainService: DomainDefinitionService,
    public formBuilder: FormBuilder,
    public service: OrganizationService,
    public authService: AuthService) { }

    ngOnInit() {

        this.loadFinancialCompliance(this.organization.id);
        this.constructForm();

        //this.orgForms = new FormArray([]);
        //this.orgForms.push(this.orgForm);

        if (!this.organization.address) this.organization.address = this.blankAddress;
        this.subscriptions.add(this.domainService.getStates().subscribe(states => this.states = states));

        //initialize the parent org and temporary org
        //if (this.parentOrganization && !this.parentOrganization.address)
        //    this.parentOrganization.address = this.blankAddress;

        this.parentOrganization = new Organization;
        this.parentOrganization.address = this.blankAddress;

        this.temporaryOrganization = new Organization;
        this.temporaryOrganization.address = this.blankAddress;

        //If this org has a parent one, build its forms
        //if (this.organization.isDbaName && this.showDba)
        //{
        //    this.constructParentOrgForm();
        //  }

        this.constructParentOrgForm();

        this.emergencyContact = this.organization.contacts.filter(c => (c.agentType & AgentType.EmergencyPOC) !== 0)[0]
        if (!this.emergencyContact) {
          this.emergencyContact = new Contact();
          this.emergencyContactExists = false;
        }
        else
          this.emergencyContactExists = true;

        this.primaryContact = this.organization.contacts.filter(c => (c.agentType & AgentType.PrimaryContact) !== 0)[0];
        if (!this.primaryContact) {
          this.primaryContact = new Contact();
          this.primaryContactExists = false;
        }
        else
          this.primaryContactExists = true;

        //Go through and reveal all the validation errors
        this.isValid();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

  constructForm(): void {
    this.orgForm = this.formBuilder.group({
      name: ["", Validators.required],
      addressStreet: ["", Validators.required],
      addressSuite: [""],
      addressPoBox: [""],
      addressCity: ["", Validators.required],
      addressStateCode: ["", Validators.required],
      addressZipCode: ["", [Validators.required, Validators.pattern(ValidatorPatterns.ZipCode)]],
      phone: ["", [Validators.required, Validators.pattern(ValidatorPatterns.Phone)]],
      fax: ["", Validators.pattern(ValidatorPatterns.Phone)],
      email: ["", Validators.pattern(ValidatorPatterns.Email)],
    });

      if (this.organization.noPhone === true) {
          this.orgForm.get("phone").clearValidators();
          this.orgForm.get("phone").setValidators([Validators.pattern(ValidatorPatterns.Phone)]);          
      }
  }

  constructParentOrgForm(): void {
    this.orgForm.addControl("parentOrganizationName", new FormControl("", this.displayDbaContactTab() ? Validators.required : null));
    this.orgForm.addControl("parentAddressStreet", new FormControl("", this.displayDbaContactTab() ? Validators.required : null));
    this.orgForm.addControl("parentAddressSuite", new FormControl(""));
    this.orgForm.addControl("parentAddressPoBox", new FormControl(""));
    this.orgForm.addControl("parentAddressCity", new FormControl("", this.displayDbaContactTab() ? Validators.required : null));
    this.orgForm.addControl("parentAddressStateCode", new FormControl("", this.displayDbaContactTab() ? Validators.required : null));
    this.orgForm.addControl("parentAddressZipCode", new FormControl("", this.displayDbaContactTab() ? [Validators.required, Validators.pattern(ValidatorPatterns.ZipCode)] : null));
    this.orgForm.addControl("parentPhone", new FormControl("", this.displayDbaContactTab() ? Validators.pattern(ValidatorPatterns.Phone) : null));
    this.orgForm.addControl("parentFax", new FormControl("", this.displayDbaContactTab() ? Validators.pattern(ValidatorPatterns.Phone) : null));
    this.orgForm.addControl("parentEmail", new FormControl("", this.displayDbaContactTab() ? Validators.pattern(ValidatorPatterns.Email) : null));
  }

  //Only display the individual contacts if the user's internal status matches that of the org
  displayContacts(): boolean {
    return this.authService.User.isInternal == this.organization.isInternal
  }

  displayDbaContactTab(): boolean {
      return this.showDba && this.organization.isDbaName && this.organization.parentOrganizationId !== undefined
                && this.organization.parentOrganizationId !== null && this.organization.parentOrganizationId !== "";
  }

  get isInternalUser(): boolean {
    return this.authService.User.isInternal;
    }

    loadFinancialCompliance(organization: string): void {
        let spec = {
            organizationIds: [],
            includeHistoricOwnerNumbers: false,
            organizationId: organization,
            historicOwnerNumber: "",
            exactMatch: true
        } as FinancialComplianceSearchSpec;

        let detail$ = this.service.getComplianceData(spec).pipe(
            map((details: OrganizationComplianceDetails[]) => {
                if (details != null && details.length > 0) return details[0]; //should ever only be one entry for an organization by id since its used as primary key
                return null;
            }),
            tap((summary: OrganizationComplianceDetails) => {
                this.orgCompliance = summary;
            }),
            finalize(() => { this.loadingCompliance.next(false); })
        ).subscribe(
            () => { },
            (error) => { console.error(error); }
        );
        this.subscriptions.add(detail$);
    }

  reuseMainOrgForDbaChange(event: any): void {
    var value = event.target.value;
    this.dbaReuseParentInfo = JSON.parse(value);

    //If they choose to reuse the values from main org, set this
    if (this.dbaReuseParentInfo) {
      //save off current values
      this.setOrgDetails(this.parentOrganization, this.temporaryOrganization);
      //set them
      this.setOrgDetails(this.organization, this.parentOrganization);
    }
    //If they want to revert away from main org values, set this
    else {
      //set them
      this.setOrgDetails(this.temporaryOrganization, this.parentOrganization);
    }
  }

  setOrgDetails(sourceOrg: Organization, destOrg: Organization) {
    destOrg.address = sourceOrg.address;
    destOrg.phone = sourceOrg.phone;
    destOrg.fax = sourceOrg.fax;
    destOrg.email = sourceOrg.email;
  }

  getTypeDescription(code: string): string {
    return this.service.getTypeDescription(code);
  }

  getComplianceStatusDescription(status: number): string {
    return status ? 'Out of Compliance' : 'Compliant';
    }

  get complianceStatus(): boolean {
    let complianceStatus = null;
    if (this.orgCompliance) {
        complianceStatus = (this.orgCompliance.isCompliant);
    }

    return complianceStatus;
  }

    get complianceStatusText(): string {
        let text: string = "Unavailable";
        if (this.loadingCompliance.value == true) text = "Loading Compliance...";
        else if (this.isRecalculating) text = 'Calculating...';
        else if (this.complianceStatus != null)
            text = this.complianceStatus ? 'Compliant' : 'Out of Compliance';
        return text;
    }

  getDbaStatusDescription(): string {
    return this.organization.isDbaName ? 'Yes' : 'No';
  }

  isValid(): boolean {
    if (this.orgForm.invalid) {
      // Touch all the controls to reveal validation errors
      Object.keys(this.orgForm.controls).forEach(field => {
        const control = this.orgForm.get(field);
        control.markAsTouched({ onlySelf: true });
      });

      return false;
    } else {
      return true;
    }
  }

  onTabClicked(): void {
  }

}
